import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { X } from "lucide-react";
import { supabase } from "@/lib/supabaseClient";
import { formatDoctorName } from "@/lib/doctorName";

// =====================================================================
// AddPatientDialog — Phase 5 R1
// Fields: Full Name*, Phone*, Email, Preferred Doctor, Preferred
// Contact Method, Recall opt-in checkbox.
// Phone duplicates DO NOT block submit (families share phones); we
// surface a non-blocking warning toast.
// =====================================================================

const CONTACT_METHODS = [
  { value: "no_preference", label: "No preference" },
  { value: "phone",         label: "Phone call" },
  { value: "sms",           label: "SMS" },
  { value: "email",         label: "Email" },
];

export default function AddPatientDialog({ open, onOpenChange, existingPhones = [], tutorialDemo = false, onCreated }) {
  const [doctors, setDoctors] = useState([]);
  const [doctorsLoading, setDoctorsLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState(initialForm());

  const reset = useCallback(() => setForm(initialForm()), []);

  useEffect(() => {
    if (!open) return;
    reset();
    setDoctorsLoading(true);
    supabase.rpc("list_clinic_doctors").then(({ data, error }) => {
      setDoctorsLoading(false);
      if (error) {
        // Non-blocking: dialog still works without dropdown options.
        console.warn("[patients] list_clinic_doctors failed", error);
        setDoctors([]);
        return;
      }
      setDoctors(data ?? []);
    });
  }, [open, reset]);

  const close = () => { if (!submitting) onOpenChange(false); };

  function updateField(field, value) {
    setForm((current) => ({ ...current, [field]: value }));
    if (field === "full_name") {
      dispatchTutorialTypingEvent("tutorial:patient-registration-full-name-typed", value);
    } else if (field === "phone") {
      dispatchTutorialTypingEvent("tutorial:patient-registration-phone-typed", value);
    }
  }

  async function submit(e) {
    e.preventDefault();
    if (submitting) return;
    const fullName = form.full_name.trim();
    const phone    = form.phone.trim();
    if (!fullName) { toast.error("Full name is required."); return; }
    if (!phone)    { toast.error("Phone is required."); return; }

    // Family-share warning: existing patient with same phone in current list.
    if (existingPhones.includes(phone)) {
      toast.warning("Heads up: another patient already uses this phone. Submitting anyway (families can share phones).");
    }

    if (tutorialDemo) {
      toast.success("Practice patient complete. Nothing was added to the clinic patient list.");
      dispatchFirstPatientCreated({ id: "tutorial-patient-registration", tutorial_demo: true });
      onOpenChange(false);
      return;
    }

    setSubmitting(true);
    const payload = {
      full_name: fullName,
      phone,
      email: form.email.trim() || null,
      date_of_birth: form.date_of_birth || null,
      preferred_doctor_id: form.preferred_doctor_id || null,
      preferred_contact_method: form.preferred_contact_method,
      recall_opt_in: form.recall_opt_in,
    };
    const { data, error } = await supabase.rpc("create_patient", { p_data: payload });
    if (error) {
      setSubmitting(false);
      toast.error(error.message ?? "Could not create patient.");
      return;
    }
    dispatchFirstPatientCreated(data);

    // Optional follow-up: set messenger_conversation_url if the user
    // entered one. Failure here doesn't roll back the patient creation;
    // we surface a warning toast and let them set it later.
    const messengerUrl = form.messenger_conversation_url.trim();
    if (messengerUrl && data?.id) {
      const { error: mErr } = await supabase.rpc("update_patient_messenger_url", {
        p_patient_id: data.id,
        p_url:        messengerUrl,
      });
      if (mErr) {
        toast.warning("Patient created, but Messenger URL didn't save: " + (mErr.message ?? "unknown error"));
      }
    }

    setSubmitting(false);
    onOpenChange(false);
    onCreated?.(data);
  }

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="add-patient-dialog"
      data-tutorial-id="add-patient-dialog"
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm p-4"
      onMouseDown={(e) => { if (e.target === e.currentTarget) close(); }}
    >
      <div
        data-testid="add-patient-dialog-panel"
        className="flex max-h-[calc(100dvh-2rem)] w-full max-w-lg flex-col overflow-hidden rounded-2xl bg-white shadow-xl"
      >
        <div className="shrink-0 px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <div>
            <h2 className="text-base font-bold text-slate-900">{tutorialDemo ? "Practice adding a patient" : "Add Patient"}</h2>
            <p className="text-xs text-slate-500 mt-0.5">
              {tutorialDemo ? "Use sample details. Tutorial mode will not create a patient record." : "Create a new patient record. Required fields are marked with *."}
            </p>
          </div>
          <button type="button" onClick={close} disabled={submitting} data-testid="add-patient-close" className="p-1 rounded hover:bg-slate-100 text-slate-500">
            <X className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={submit} className="flex-1 overflow-y-auto px-6 py-5 space-y-4" data-testid="add-patient-form">
          <Field label="Full name *" testid="full_name">
            <input
              type="text"
              autoFocus
              value={form.full_name}
              onChange={(e) => updateField("full_name", e.target.value)}
              data-testid="add-patient-full-name-input"
              data-tutorial-id="add-patient-full-name-input"
              className="w-full px-3 py-2 text-sm rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-teal-500/40 focus:border-teal-500"
              placeholder="Juan Dela Cruz"
            />
          </Field>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Phone *" testid="phone">
              <input
                type="tel"
                value={form.phone}
                onChange={(e) => updateField("phone", e.target.value)}
                data-testid="add-patient-phone-input"
                data-tutorial-id="add-patient-phone-input"
                className="w-full px-3 py-2 text-sm rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-teal-500/40 focus:border-teal-500"
                placeholder="09171234567"
              />
            </Field>
            <Field label="Email" testid="email">
              <input
                type="email"
                value={form.email}
                onChange={(e) => updateField("email", e.target.value)}
                data-testid="add-patient-email-input"
                data-tutorial-id="add-patient-email-input"
                className="w-full px-3 py-2 text-sm rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-teal-500/40 focus:border-teal-500"
                placeholder="juan.delacruz@example.com"
              />
            </Field>
          </div>

          <Field label="Date of birth" testid="dob">
            <input
              type="date"
              value={form.date_of_birth}
              onChange={(e) => updateField("date_of_birth", e.target.value)}
              data-testid="add-patient-dob-input"
              data-tutorial-id="add-patient-dob-input"
              className="w-full px-3 py-2 text-sm rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-teal-500/40 focus:border-teal-500"
            />
          </Field>

          <Field label="Preferred doctor" testid="preferred_doctor">
            <select
              value={form.preferred_doctor_id}
              onChange={(e) => updateField("preferred_doctor_id", e.target.value)}
              data-testid="add-patient-doctor-select"
              data-tutorial-id="add-patient-doctor-select"
              className="w-full px-3 py-2 text-sm rounded-lg border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-teal-500/40 focus:border-teal-500"
            >
              <option value="">{doctorsLoading ? "Loading…" : "No preference"}</option>
              {doctors.map((d) => (
                <option key={d.user_id} value={d.user_id}>{formatDoctorName(d)}</option>
              ))}
            </select>
          </Field>

          <Field label="Messenger conversation URL" testid="messenger_url">
            <input
              type="url"
              value={form.messenger_conversation_url}
              onChange={(e) => updateField("messenger_conversation_url", e.target.value)}
              data-testid="add-patient-messenger-url-input"
              data-tutorial-id="add-patient-messenger-url-input"
              className="w-full px-3 py-2 text-sm rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-teal-500/40 focus:border-teal-500"
              placeholder="https://business.facebook.com/latest/inbox/all/?…"
            />
            <p className="text-[11px] text-slate-500 mt-1">Optional. Paste the patient's Facebook Messenger conversation link from Meta inbox so the Recall Center can jump straight to the chat later.</p>
          </Field>

          <Field label="Preferred contact method" testid="preferred_contact">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" role="radiogroup" data-testid="add-patient-contact-method-group" data-tutorial-id="add-patient-contact-method-group">
              {CONTACT_METHODS.map((m) => {
                const selected = form.preferred_contact_method === m.value;
                return (
                  <button
                    type="button"
                    key={m.value}
                    onClick={() => updateField("preferred_contact_method", m.value)}
                    data-testid={`add-patient-contact-method-${m.value}`}
                    aria-pressed={selected}
                    className={`text-xs font-medium px-3 py-2 rounded-lg border transition-colors ${selected ? "bg-teal-600 text-white border-teal-600" : "bg-white text-slate-700 border-slate-200 hover:border-teal-300"}`}
                  >
                    {m.label}
                  </button>
                );
              })}
            </div>
          </Field>

          <label className="flex items-start gap-3 cursor-pointer select-none" data-testid="add-patient-recall-row" data-tutorial-id="add-patient-recall-row">
            <input
              type="checkbox"
              checked={form.recall_opt_in}
              onChange={(e) => updateField("recall_opt_in", e.target.checked)}
              data-testid="add-patient-recall-checkbox"
              className="mt-0.5 w-4 h-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
            />
            <span className="text-sm text-slate-700 leading-snug">
              Include this patient in recall outreach
              <span className="block text-xs text-slate-500 mt-0.5">When dormant, the recall job will queue them for follow-up.</span>
            </span>
          </label>

          <div
            data-testid="add-patient-actions"
            className="sticky bottom-0 -mx-6 -mb-5 flex items-center justify-end gap-2 border-t border-slate-100 bg-white/95 px-6 py-4 backdrop-blur"
          >
            <button
              type="button"
              onClick={close}
              disabled={submitting}
              data-testid="add-patient-cancel-button"
              className="px-3.5 py-2 text-sm font-medium rounded-lg border border-slate-200 text-slate-700 bg-white hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              data-testid="add-patient-submit-button"
              data-tutorial-id="add-patient-submit-button"
              className="px-4 py-2 text-sm font-semibold rounded-lg bg-teal-600 text-white hover:bg-teal-700 transition-colors shadow-sm disabled:opacity-60"
            >
              {submitting ? "Creating…" : tutorialDemo ? "Finish practice" : "Create patient"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function dispatchTutorialTypingEvent(eventName, value) {
  if (typeof window === "undefined") return;
  if (!String(value || "").trim()) return;
  window.dispatchEvent(new CustomEvent(eventName));
}

function dispatchFirstPatientCreated(patient) {
  if (typeof window === "undefined") return;
  const patientId = patient?.patient_id || patient?.id;
  if (!patientId) return;
  window.dispatchEvent(new CustomEvent("tutorial:first-patient-created", {
    detail: { patient_id: patientId, ...(patient?.tutorial_demo ? { tutorial_demo: true } : {}) },
  }));
}

function Field({ label, children, testid }) {
  return (
    <div data-testid={`add-patient-field-${testid}`}>
      <label className="block text-xs font-semibold text-slate-700 mb-1.5">{label}</label>
      {children}
    </div>
  );
}

function initialForm() {
  return {
    full_name: "",
    phone: "",
    email: "",
    date_of_birth: "",
    preferred_doctor_id: "",
    preferred_contact_method: "no_preference",
    recall_opt_in: true,
    messenger_conversation_url: "",
  };
}
